import { inject } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, mergeMap, of } from 'rxjs';

import { RefundBlueSageTransactionGQL } from 'src/generated/graphql.generated';

import { selectTransactionJob } from '../../transactions/transaction-job.selectors';
import { blueSageRefundLegsOf } from '../bluesage.util';

import { BlueSageRefundDialogActions } from './bluesage-refund-dialog.actions';
import { blueSageRefundDialogFeature } from './bluesage-refund-dialog.feature';
import { BLUESAGE_PROVIDER, composeRefundReason } from './bluesage-refund-dialog.state';

/**
 * Perform the refund.
 *
 * `exhaustMap`, not `switchMap` — this moves money. A double-click must not
 * become two refunds. The server's K28 guard refuses the second anyway, but the
 * UI should not rely on that to stay correct, and the two defences close
 * different windows: this one closes the double-click, the server's closes the
 * second tab and the second operator.
 *
 * The amount is sent ONLY when it is genuinely partial. An absent amount means
 * "the full remaining balance", which is the correct instruction for a full
 * refund — and on a standalone charge it is the difference between a refund
 * that works and one refused outright, because the middleware reads "amount
 * present" as "this is a partial".
 */
export const submitBlueSageRefund = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    refundGQL = inject(RefundBlueSageTransactionGQL),
) => {
    return actions$.pipe(
        ofType(BlueSageRefundDialogActions.confirmButtonClicked),
        exhaustMap((action) => of(action).pipe(
            concatLatestFrom(() => [
                store.select(blueSageRefundDialogFeature.selectBlueSageRefundDialogState),
                store.select(selectTransactionJob),
            ]),
            mergeMap(([ , state, job ]) => refundGQL.mutate({
                transactionId: state.transactionId,
                ...(state.amountMinor < state.refundableMinor
                    ? { amount: state.amountMinor }
                    : {}),
                // Required now, and composed from the category and the words
                // beside it: `Damage — sofa arm torn in transit`. The confirm
                // button cannot be pressed without one, so the spread is belt
                // and braces rather than a real optionality.
                ...(composeRefundReason(state.reasonCategory, state.reasonDetail)
                    ? { reason: composeRefundReason(state.reasonCategory, state.reasonDetail) }
                    : {}),
                // Only meaningful on a full refund; the server ignores it on a
                // partial, which never returns a tip. Sent unconditionally so the
                // wire says what the operator chose rather than what we inferred.
                refundTip: state.refundTip,
            }).pipe(
                map(() => BlueSageRefundDialogActions.refundSuccess({
                    // The mutation's contract is `Boolean!` — deliberately unchanged,
                    // because widening it would be a breaking schema change to vendor
                    // code. The rail lands on the transaction as
                    // `metadata.bluesageRefundType` and the row reads it from there,
                    // so nothing here guesses.
                    rail: undefined,
                    transactionId: state.transactionId,
                    // Read BEFORE the mutation ran: the new leg can reach this
                    // browser over the job subscription before the response does,
                    // and it must not be mistaken for one that was already there.
                    knownLegIds: blueSageRefundLegsOf(job?.transactions, state.transactionId)
                        .map((leg) => leg.id),
                    awaitsConfirmation: state.provider === BLUESAGE_PROVIDER,
                })),
            )),
            catchError((error) => of(BlueSageRefundDialogActions.refundError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });
